import axios from "axios";
import { useAuthStore } from "@/stores/auth";

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "/api/v1";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

api.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  if (authStore.tokens?.access_token) {
    config.headers.Authorization = `Bearer ${authStore.tokens.access_token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const authStore = useAuthStore();

      // If the refresh endpoint itself returns 401, don't try to refresh again —
      // that would deadlock because isRefreshing is already true.
      if (originalRequest.url?.includes("/auth/refresh")) {
        authStore.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (!authStore.tokens?.refresh_token) {
        authStore.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for refresh to complete
        return new Promise((resolve) => {
          addRefreshSubscriber((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const success = await authStore.refreshAccessToken();
        if (success && authStore.tokens?.access_token) {
          const newToken = authStore.tokens.access_token;
          onRefreshed(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed — logout handled in refreshAccessToken
      } finally {
        isRefreshing = false;
      }

      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);
